import React, { useState, useEffect, useMemo, useCallback, useRef, type RefObject } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, useCollectionBinding, RichTextContent } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import type { Amenity } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { GlowTrace } from './GlowTrace';

// ============================================================================
// Types for collection-bound gallery images
// ============================================================================

interface OverviewGalleryImage {
  image: string;
  alt_text?: string;
  title?: string;
  description?: string;
}

const OVERVIEW_AMENITY_TWO_COL_AT = 6;

// ============================================================================
// Slot Group Definitions
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'See All Availability' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const leadImageDef: SlotGroupDefinition = {
  label: 'Lead Lifestyle Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://images.unsplash.com/photo-1450778869180-41d0601e046e?auto=format&fit=crop&w=1600&q=80',
    },
  },
};

// ============================================================================
// Image Slider Sub-component
// ============================================================================

function GallerySlider({ photos }: { photos: OverviewGalleryImage[] }) {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const count = photos.length;

  const goTo = useCallback((idx: number) => {
    if (isTransitioning || count <= 1) return;
    setIsTransitioning(true);
    setCurrent(idx);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning, count]);

  const next = useCallback(() => goTo((current + 1) % count), [current, count, goTo]);
  const prev = useCallback(() => goTo((current - 1 + count) % count), [current, count, goTo]);

  useEffect(() => {
    if (count <= 1) return;
    timerRef.current = setTimeout(next, 5000);
    return () => clearTimeout(timerRef.current);
  }, [current, count, next]);

  if (!count) return <div className="w-full h-full bg-[var(--vibe-color-surface)]" />;

  return (
    <div className="relative w-full h-full overflow-hidden rounded-sm group">
      {photos.map((photo, i) => (
        <VibesitesImage
          key={i}
          url={photo.image || ''}
          alt={photo.alt_text || photo.title || ''}
          sizes="(min-width: 1024px) 40vw, 100vw"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-600 ease-in-out ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}

      {/* Brand-tinted overlay at bottom for depth */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />

      {count > 1 && (
        <>
          {/* Prev / Next arrows */}
          <button
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
            aria-label="Previous image"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>
          <button
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
            aria-label="Next image"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>

          {/* Dots */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {photos.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === current ? 'bg-white w-5' : 'bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`Go to image ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function PropertyOverview() {
  const description = useContentToken('propertyoverview.description');
  const ctaLink = useSlotGroup('propertyoverview.cta_link', ctaLinkDef);
  const leadImage = useSlotGroup('propertyoverview.lead_image', leadImageDef);
  const tabInteriorLabel = useContentToken('propertyoverview.tab_interior_label');
  const tabCommunityLabel = useContentToken('propertyoverview.tab_community_label');

  // Use useCollectionBinding instead of useGallery so the image slider
  // is editable in the Vibesites content editor (hide, reorder, add custom images).
  const galleryImages = useCollectionBinding<OverviewGalleryImage>('propertyoverview.gallery_images', {
    collection: 'photo_gallery',
    defaultLimit: 12,
  });
  const apartmentAmenities = useCollectionBinding<Amenity>('propertyoverview.apartment_amenities', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });
  const communityAmenities = useCollectionBinding<Amenity>('propertyoverview.community_amenities', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const sliderPhotos = useMemo(() => {
    const gallery = (galleryImages || []).filter((p) => p.image);
    if (!leadImage.image) return gallery;
    return [
      {
        image: leadImage.image,
        alt_text: 'Dog and cat cuddling outdoors — a pet-friendly lifestyle moment',
        title: 'Lifestyle',
        description: 'Pet lifestyle',
      },
      ...gallery.filter((p) => p.image !== leadImage.image),
    ];
  }, [galleryImages, leadImage.image]);

  const [activeTab, setActiveTab] = useState<'interior' | 'community'>('interior');
  const [descExpanded, setDescExpanded] = useState(false);
  const showInteriorSelectHomesDisclaimer = useMemo(
    () => apartmentAmenities.some((item) => item.title?.includes('*')),
    [apartmentAmenities],
  );
  const interiorSelectHomesDisclaimer = useContentToken('propertyoverview.interior_select_homes_disclaimer');

  const activeItems = activeTab === 'interior' ? apartmentAmenities : communityAmenities;
  const useTwoColAmenities = activeItems.length >= OVERVIEW_AMENITY_TWO_COL_AT;

  useRevealStyles();
  const accentRef = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

          {/* ============================================================ */}
          {/* Left Column: Text + Amenity Tabs                              */}
          {/* ============================================================ */}
          <div ref={accentRef as RefObject<HTMLDivElement>} className="reveal-fade-up">
            {/* Accent line draws in above the eyebrow */}
            <svg className="accent-line accent-line--delay-1 mb-4" style={{ '--line-length': '64', width: '64px', height: '3px' } as React.CSSProperties} viewBox="0 0 64 3" preserveAspectRatio="none">
              <line x1="0" y1="1.5" x2="64" y2="1.5" strokeWidth="2" />
            </svg>

            <RichTextContent
              path="propertyoverview.pre_title"
              label="Eyebrow Text"
              defaultValue="<p>Your Search Ends Here</p>"
              as="p"
              className="font-body text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--vibe-color-primary)] mb-3"
            />

            <RichTextContent
              path="propertyoverview.title"
              label="Title"
              defaultValue="<p>Experience the charm.</p>"
              as="h2"
              className="font-heading text-3xl sm:text-4xl md:text-[2.75rem] font-normal text-[var(--vibe-color-text)] mb-5 leading-[1.15]"
            />

            <div className="mb-10 max-w-lg">
              <div className={`overview-desc-wrap ${descExpanded ? 'overview-desc-wrap--open' : ''}`}>
                <RichTextContent
                  path="propertyoverview.description"
                  label="Description"
                  defaultValue="<p>Welcome to our residential community featuring spacious layouts and thoughtful amenities. Discover the perfect blend of comfort, style, and convenience.</p>"
                  as="div"
                  className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed"
                />
              </div>
              {description && String(description).length > 200 && (
                <button
                  onClick={() => setDescExpanded(!descExpanded)}
                  className="mt-2 font-body text-sm font-semibold text-[var(--vibe-color-text)] hover:underline transition-colors"
                >
                  {descExpanded ? (
                    <RichTextContent
                      path="propertyoverview.read_less_label"
                      label="Read Less Label"
                      defaultValue="<p>Read Less</p>"
                      as="span"
                    />
                  ) : (
                    <RichTextContent
                      path="propertyoverview.read_more_label"
                      label="Read More Label"
                      defaultValue="<p>Read More</p>"
                      as="span"
                    />
                  )}
                </button>
              )}
            </div>

            {/* Amenity Tabs */}
            {(apartmentAmenities.length > 0 || communityAmenities.length > 0) && (
              <div className="mb-8 max-w-lg">
                <div className="overview-tab-row flex flex-wrap gap-2">
                  {apartmentAmenities.length > 0 && (
                    <button
                      onClick={() => setActiveTab('interior')}
                      className={`overview-tab ${activeTab === 'interior' ? 'overview-tab--active' : ''}`}
                    >
                      <EditableToken path="propertyoverview.tab_interior_label" defaultValue="Interior Features" as="span">
                        {tabInteriorLabel}
                      </EditableToken>
                    </button>
                  )}
                  {communityAmenities.length > 0 && (
                    <button
                      onClick={() => setActiveTab('community')}
                      className={`overview-tab ${activeTab === 'community' ? 'overview-tab--active' : ''}`}
                    >
                      <EditableToken path="propertyoverview.tab_community_label" defaultValue="Community Amenities" as="span">
                        {tabCommunityLabel}
                      </EditableToken>
                    </button>
                  )}
                </div>

                <div className="pt-4">
                  <ul
                    className={
                      useTwoColAmenities
                        ? 'overview-amenity-list overview-amenity-list--two-col'
                        : 'overview-amenity-list'
                    }
                  >
                    {activeItems.map((a, i) => (
                      <li key={`${activeTab}-${i}`} className="overview-amenity-item flex items-start gap-2.5 text-sm font-body text-[var(--vibe-color-text-muted)] leading-snug">
                        <span className="overview-bullet mt-1.5 flex-shrink-0" />
                        <span>{a.title}</span>
                      </li>
                    ))}
                  </ul>
                  {activeTab === 'interior' && showInteriorSelectHomesDisclaimer && (
                    <EditableToken
                      path="propertyoverview.interior_select_homes_disclaimer"
                      as="p"
                      className="overview-amenity-disclaimer"
                    >
                      {interiorSelectHomesDisclaimer || '* Available in select homes'}
                    </EditableToken>
                  )}
                </div>
              </div>
            )}

            {/* CTA */}
            <EditableSlotGroup groupPath="propertyoverview.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/floor-plans'}
                target={ctaLink._target || '_self'}
                className="overview-cta-btn inline-flex items-center gap-2"
              >
                <span>{ctaLink.label || 'See All Availability'}</span>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </a>
            </EditableSlotGroup>
          </div>

          {/* ============================================================ */}
          {/* Right Column: Gallery Slider with corner accents              */}
          {/* ============================================================ */}
          <div className="glow-trace-wrap relative w-full aspect-[4/3] lg:aspect-[3/4] lg:max-h-[640px] rounded-sm overflow-hidden">
            <GlowTrace delay={0.5} />
            {/* Lead lifestyle image is editable; rendered as the first slider slide. */}
            <EditableSlotGroup
              groupPath="propertyoverview.lead_image"
              definition={leadImageDef}
              className="sr-only"
            >
              <VibesitesImage
                url={leadImage.image || ''}
                alt="Dog and cat cuddling outdoors — a pet-friendly lifestyle moment"
                sizes="(min-width: 1024px) 40vw, 100vw"
              />
            </EditableSlotGroup>
            <GallerySlider photos={sliderPhotos} />
          </div>
        </div>
      </div>

      <style>{`
        /* Description expand / collapse */
        .overview-desc-wrap {
          max-height: 9.6em;
          overflow: hidden;
          transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          -webkit-mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
          mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
        }
        .overview-desc-wrap--open {
          max-height: 60em;
          -webkit-mask-image: none;
          mask-image: none;
        }

        /* Tabs */
        .overview-tab {
          padding: 0.5rem 1.25rem;
          font-size: 0.6875rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
          background: transparent;
          border: 1px solid var(--vibe-color-border);
          border-radius: 9999px;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .overview-tab:hover {
          color: var(--vibe-color-text-on-primary);
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
        }
        .overview-tab--active {
          color: var(--vibe-color-text-on-primary);
          background: linear-gradient(
            135deg,
            var(--vibe-color-primary) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 78%, black) 100%
          );
          border-color: transparent;
          box-shadow: 0 1px 6px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .overview-tab--active:hover {
          color: var(--vibe-color-text-on-primary);
          border-color: transparent;
        }

        /* Bullet */
        .overview-bullet {
          display: block;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--vibe-color-primary);
          opacity: 0.6;
          flex-shrink: 0;
        }

        .overview-amenity-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .overview-amenity-list--two-col {
          display: block;
          column-count: 1;
          column-gap: 1rem;
        }
        @media (min-width: 640px) {
          .overview-amenity-list--two-col {
            column-count: 2;
          }
        }
        .overview-amenity-item {
          break-inside: avoid;
          -webkit-column-break-inside: avoid;
          page-break-inside: avoid;
          margin-bottom: 0.5rem;
        }
        .overview-amenity-list--two-col .overview-amenity-item:last-child {
          margin-bottom: 0;
        }

        .overview-amenity-disclaimer {
          margin: 0.75rem 0 0;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          line-height: 1.45;
          color: var(--vibe-color-text-muted);
        }

        /* CTA Button */
        .overview-cta-btn {
          padding: 0.875rem 2rem;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-secondary);
          background: linear-gradient(135deg, var(--vibe-color-secondary) 0%, color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%);
          border-radius: 9999px;
          transition: all 0.3s ease;
        }
        .overview-cta-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 35%, transparent);
        }

        /* Slider transition */
        .duration-600 {
          transition-duration: 600ms;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.pre_title': {
      label: 'Eyebrow Text',
      type: 'richtext',
      defaultValue: '<p>Your Search Ends Here</p>',
    },
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultField: 'property_home_hero_title',
      defaultValue: '<p>Experience the charm.</p>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue: '<p>Welcome to our residential community featuring spacious layouts and thoughtful amenities.</p>',
    },
    'propertyoverview.tab_interior_label': {
      label: 'Interior Tab Label',
      type: 'text',
      defaultValue: 'Interior Features',
    },
    'propertyoverview.tab_community_label': {
      label: 'Community Tab Label',
      type: 'text',
      defaultValue: 'Community Amenities',
    },
    'propertyoverview.interior_select_homes_disclaimer': {
      label: 'Interior Select Homes Disclaimer',
      type: 'text',
      defaultValue: '* Available in select homes',
    },
    'propertyoverview.read_more_label': {
      label: 'Read More Label',
      type: 'richtext',
      defaultValue: '<p>Read More</p>',
    },
    'propertyoverview.read_less_label': {
      label: 'Read Less Label',
      type: 'richtext',
      defaultValue: '<p>Read Less</p>',
    },
  },
  slotGroups: {
    'propertyoverview.cta_link': {
      label: 'CTA Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
        label: { label: 'Button Text', type: 'text', defaultValue: 'See All Availability' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'propertyoverview.lead_image': {
      label: 'Lead Lifestyle Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultValue: 'https://images.unsplash.com/photo-1450778869180-41d0601e046e?auto=format&fit=crop&w=1600&q=80',
        },
      },
    },
  },
  collectionBindings: {
    'propertyoverview.gallery_images': {
      label: 'Overview Slideshow Images',
      collection: 'photo_gallery',
      defaultLimit: 12,
    },
    'propertyoverview.apartment_amenities': {
      label: 'Apartment Amenities',
      collection: 'amenities',
      filter: { group: 'Apartment' },
    },
    'propertyoverview.community_amenities': {
      label: 'Community Amenities',
      collection: 'amenities',
      filter: { group: 'Community' },
    },
  },
};

(PropertyOverview as any).contentSchema = contentSchema;

export default PropertyOverview;
